import React, { useState } from 'react';

const Sidebar = ({ currentChatId, chats }) => (
  <aside className="w-64 bg-gray-900 text-gray-100 flex flex-col h-full transition-all">
    {/* Header */}
    <div className="p-4">
      <h1 className="text-xl font-bold flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
          <span className="text-xs">AI</span>
        </div>
        AI Assistant
      </h1>
      <button className="w-full flex items-center gap-3 px-3 py-2 border border-gray-700 rounded-md hover:bg-gray-800 transition-colors text-sm font-medium">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
        </svg>
        New Chat
      </button>
    </div>

    {/* Chat History */}
    <nav className="flex-1 overflow-y-auto px-2 space-y-1 custom-scrollbar">
      <p className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 mt-4">Recent</p>
      {chats.map((chat) => (
        <button
          key={chat.id}
          className={`w-full text-left px-3 py-2 rounded-md text-sm truncate transition-colors ${
            chat.id === currentChatId ? 'bg-gray-800 text-white' : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
          }`}
        >
          {chat.title}
        </button>
      ))}
    </nav>

    {/* User Profile */}
    <div className="p-4 border-t border-gray-800">
      <div className="flex items-center gap-3 p-2 hover:bg-gray-800 rounded-lg cursor-pointer transition-colors">
        <div className="w-9 h-9 bg-gradient-to-tr from-purple-500 to-blue-500 rounded-full flex items-center justify-center text-xs font-bold">
          JD
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">John Doe</p>
          <p className="text-xs text-gray-500 truncate">Pro Plan</p>
        </div>
      </div>
    </div>
  </aside>
);

const ChatHeader = ({ modelName }) => (
  <header className="h-16 border-b border-gray-200 bg-white/80 backdrop-blur-md flex items-center justify-between px-6 sticky top-0 z-10">
    <div className="flex items-center gap-3">
      <h2 className="font-semibold text-gray-800">{modelName}</h2>
      <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px] font-bold uppercase tracking-wider">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
        Online
      </span>
    </div>
    <button className="text-gray-500 hover:text-gray-700 transition-colors">
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z" />
      </svg>
    </button>
  </header>
);

const MessageBubble = ({ message, isAi }) => (
  <div className={`flex w-full mb-6 ${isAi ? 'justify-start' : 'justify-end'}`}>
    <div className={`flex max-w-[80%] md:max-w-[70%] ${isAi ? 'flex-row' : 'flex-row-reverse'}`}>
      {/* Avatar */}
      <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${
        isAi ? 'bg-indigo-600 text-white mr-3' : 'bg-gray-300 text-gray-600 ml-3'
      }`}>
        {isAi ? 'AI' : 'U'}
      </div>
      
      {/* Bubble */}
      <div>
        <div className={`px-4 py-3 rounded-2xl shadow-sm ${
          isAi 
            ? 'bg-white text-gray-800 rounded-tl-none border border-gray-100' 
            : 'bg-blue-600 text-white rounded-tr-none'
        }`}>
          <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.text}</p>
        </div>
        <p className={`text-[10px] mt-1.5 text-gray-400 font-medium ${isAi ? 'text-left' : 'text-right'}`}>
          {message.timestamp}
        </p>
      </div>
    </div>
  </div>
);

const ChatInput = () => {
  const [text, setText] = useState('');

  return (
    <div className="p-4 border-t border-gray-100 bg-white">
      <div className="max-w-4xl mx-auto relative flex items-end gap-2 bg-gray-50 border border-gray-200 rounded-2xl p-2 focus-within:border-blue-400 focus-within:ring-1 focus-within:ring-blue-400 transition-all">
        <textarea
          rows="1"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Message AI Assistant..."
          className="flex-1 bg-transparent border-none focus:ring-0 resize-none py-2 px-3 text-sm text-gray-700 max-h-32"
        />
        <button 
          disabled={!text.trim()}
          className="p-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
            <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
          </svg>
        </button>
      </div>
      <p className="text-[10px] text-center text-gray-400 mt-2">
        AI Assistant can make mistakes. Check important info.
      </p>
    </div>
  );
};

// --- Main Layout ---

const ChatLayout = () => {
  // Dummy Data
  const [chats] = useState([
    { id: 1, title: 'Quantum Computing Intro' },
    { id: 2, title: 'React Performance Tips' },
    { id: 3, title: 'Recipe for Sourdough' },
    { id: 4, title: 'Next.js 15 Routing' },
  ]);

  const [messages] = useState([
    { id: 1, text: "Hello! How can I help you today?", role: 'ai', timestamp: '12:00 PM' },
    { id: 2, text: "Can you explain the difference between Tailwind and traditional CSS?", role: 'user', timestamp: '12:01 PM' },
    { id: 3, text: "Tailwind CSS is a utility-first framework. Instead of writing custom CSS in separate files, you apply pre-defined classes directly to your HTML. It speeds up development and ensures consistency.", role: 'ai', timestamp: '12:01 PM' },
  ]);

  return (
    <div className="flex h-screen bg-gray-100 font-sans text-gray-900 overflow-hidden">
      <Sidebar currentChatId={1} chats={chats} />
      
      <main className="flex-1 flex flex-col h-full overflow-hidden">
        <ChatHeader modelName="Gemini AI Pro" />
        
        <div className="flex-1 overflow-y-auto px-4 md:px-0">
          <div className="max-w-3xl mx-auto py-8">
            {messages.map((msg) => (
              <MessageBubble key={msg.id} message={msg} isAi={msg.role === 'ai'} />
            ))}
          </div>
        </div>

        <ChatInput />
      </main>
    </div>
  );
};

export default ChatLayout;